import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Collapse, Table, TableBody, TableCell, TableRow } from '@mui/material';
import classnames from 'classnames';
import moment from 'moment';

import AdminSupplementStatusCount from '@components/AdminSupplementStatusCount/AdminSupplementStatusCount';
import UpDownButton from '@components/UpDownButton/UpDownButton';
import useToggle from '@hooks/useToggle';
import { TAdminInquiryEntry } from '@interfaces/adminInquiries';
import { TAdminSupplementInquiryStatus } from '@interfaces/adminSupplements';
import { TInquiryStatus } from '@interfaces/inquiries';
import {
  handleGetAdminMedicationDetails,
  handleGetAdminSupplementDetails,
} from '@store/features/adminInquiries/inquiryEntryDetails/actions';
import {
  adminMedicationDetailsSelector,
  adminSupplementDetailsSelector,
} from '@store/features/adminInquiries/inquiryEntryDetails/selectors';
import useDispatch from '@store/hooks/useDispatch';
import useOrganizationById from '@store/hooks/useOrganizationById';
import formatDate from '@utils/formatDate';
import enumKeys from '@utils/getEnumValues';
import getShortenText from '@utils/getShortenText';

import SendInquiry from '../SendInquiry/SendInquiry';
import TableGridDetailsRow from '../TableGridDetailsRow/TableGridDetailsRow';

import { ITableGridRowProps } from './TableGridRow.interface';

import './style.scss';

const TableGridRow: FC<ITableGridRowProps> = ({ className, row }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const organization = useOrganizationById(row.organizationId);
  const adminSupplementInquiryDetails = useSelector(adminSupplementDetailsSelector);
  const adminMedicationInquiryDetails = useSelector(adminMedicationDetailsSelector);
  const isOpen = useMemo(() => {
    if (row.isSupplementary) {
      return row.adminSupplementInquiryDetailsDto.some(
        value => value.supplementInquiryId === adminSupplementInquiryDetails?.supplementInquiryId,
      );
    }
    return row.adminMedicationInquiryDetailsDto.some(
      value => value.medicationInquiryId === adminMedicationInquiryDetails?.medicationInquiryId,
    );
  }, [adminSupplementInquiryDetails, adminMedicationInquiryDetails, row]);

  const [open, , , handleToggle] = useToggle(isOpen);

  const isPending = row.inquiryStatusId === TInquiryStatus.Pending;

  const isNeedAdminResponse =
    row.inquiryItems.every(inquiryItem => !inquiryItem.needsAdminResponse) &&
    row.inquiryStatusId !== TInquiryStatus.Complete;

  const getAdminSupplementInquiryStatusesCount = useCallback(
    (status: number): number =>
      row.inquiryItems.reduce((accumulator, object) => {
        if (object.adminSupplementInquiryStatusId === status) {
          return accumulator + 1;
        }
        return accumulator;
      }, 0),
    [row],
  );

  const getInquiryDetails = useCallback(
    (inquiryEntry: TAdminInquiryEntry): void => {
      if (row.isSupplementary) {
        dispatch(handleGetAdminSupplementDetails(inquiryEntry.entryInquiryId));
      } else {
        dispatch(handleGetAdminMedicationDetails(inquiryEntry.entryInquiryId));
      }
    },
    [dispatch, row],
  );

  const TAdminSupplementInquiryStatusIds = enumKeys(TAdminSupplementInquiryStatus, true);

  return (
    <>
      <TableRow
        className={classnames('admin-inquiries-table', className, {
          'admin-inquiries-table_open': open,
        })}
      >
        <TableCell>{formatDate(row.submittedOn)}</TableCell>
        <TableCell className="admin-inquiries-table__id-cell">{row.id}</TableCell>
        <TableCell>
          <Box className="admin-inquiries-table__chimps">
            {TAdminSupplementInquiryStatusIds.map(
              (idString: string) =>
                getAdminSupplementInquiryStatusesCount(+idString) > 0 && (
                  <AdminSupplementStatusCount
                    key={idString}
                    value={+idString}
                    count={getAdminSupplementInquiryStatusesCount(+idString)}
                    hideLabel
                  />
                ),
            )}
          </Box>
        </TableCell>
        <TableCell title={organization?.name}>{getShortenText(organization?.name)}</TableCell>
        <TableCell>
          {row.isSupplementary ? t('adminInquiries.supplement') : t('adminInquiries.medication')}
        </TableCell>
        <TableCell>{moment.duration(row.timeElapsed).humanize()}</TableCell>
        <TableCell>
          {isPending ? (
            <HelpOutlineIcon color="success" />
          ) : (
            <Box component="img" src="/images/img-submit-green.svg" />
          )}
        </TableCell>
        <TableCell>
          <UpDownButton onOpen={handleToggle} open={open} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell className="admin-inquiries-table__collapse" colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table size="small" aria-label="inquiry details">
              <TableBody>
                {row.inquiryItems.map(rowItem => (
                  <TableGridDetailsRow
                    key={`${rowItem.entryInquiryId}}`}
                    inquiryEntry={rowItem}
                    isSupplementaryRow={row.isSupplementary}
                    onClick={getInquiryDetails}
                  />
                ))}
                {isNeedAdminResponse && <SendInquiry row={row} />}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default TableGridRow;
